import { getKeyByItemIncludes } from '../helpers/object';
import { DjangoEmployee, Employee, parseEmployee } from './../employee';
import { applyTimezone, parseDate } from './../helpers/date';
import { DjangoProposal, PROPOSAL_TYPE, Proposal, parseProposal } from './../proposal';
import {
  SERVICE_CCB_DOWNLOADABLE_STATUSES,
  SERVICE_LIQUIDATEABLE_STATUSES,
  SERVICE_STATUS,
  isServiceGiveupable,
} from './../service/service-status.model';
import {
  EMPLOYEE_SERVICE_STATUS_BY_STEP,
  EMPLOYEE_SERVICE_STEP,
  HR_SERVICE_STATUS_BY_STEP,
  HR_SERVICE_STEP,
} from './../service/service-steps.model';
import { AbstractLoan, DjangoAbstractLoan } from './abstract-loan.model';
import { LOAN_REASON } from './loan-reason.enum';

export class Loan implements AbstractLoan {
  proposal: Proposal;
  employee: Employee;
  dueDate: Date;
  firstReferencePeriod: string;
  lastReferencePeriod: Date;
  firstDueDate: Date;
  taxAmount: number;
  totalVariableTaxAmount: number;
  fixedTaxAmount: number;
  costs: any[];
  installmentAmount: number;
  insurancePremiumAmount: number;
  totalDebtAmount: number;
  debtBalance: number;
  balance: number;
  credited: Date;
  status: SERVICE_STATUS;
  fixedTaxRate: number;
  registeredInPayroll: boolean;
  isRenegotiable: boolean;
  isRefinanceable: boolean;
  reason: LOAN_REASON;
  balanceForPrepayment: number;
  refinanceableBalance: number;
  sumPaidInstallments: number;
  paidAmount: number;
  // ABSTRACT LOAN
  created: Date;
  modified: Date;
  amount: number;
  period: number;
  interestRate: number;
  effectiveInterestRate: number;
  feeAmount: number;
  currency: string;
  score: number;
  scoreVersion: number;
  agioAmount: number;

  constructor(data: string | Loan | DjangoLoan = {}) {
    if (typeof data === 'string') {
      bindConstructorDataToInstance(this, { proposal: data });
    } else {
      bindConstructorDataToInstance(this, data);
    }
  }

  get giveupable(): boolean {
    return giveupable(this);
  }

  get liquidatable(): boolean {
    return liquidatable(this);
  }

  get ccbDownloadable(): boolean {
    return ccbDownloadable(this);
  }

  get settableAsRenegotiable(): boolean {
    return settableAsRenegotiable(this);
  }

  get settableToPrepayment(): boolean {
    return settableToPrepayment(this);
  }

  get employeeStep(): EMPLOYEE_SERVICE_STEP {
    return getKeyByItemIncludes<EMPLOYEE_SERVICE_STEP>(
      EMPLOYEE_SERVICE_STATUS_BY_STEP,
      this.status,
    );
  }

  get hrStep(): HR_SERVICE_STEP {
    return getKeyByItemIncludes<HR_SERVICE_STEP>(HR_SERVICE_STATUS_BY_STEP, this.status);
  }
}

export function parseLoan(data?: string | Loan | DjangoLoan) {
  if (data) {
    return new Loan(data);
  } else {
    return undefined;
  }
}

//////////////////////
/// DJANGO SUPPORT ///
//////////////////////
export interface DjangoLoan extends DjangoAbstractLoan {
  proposal?: DjangoProposal | string;
  employee?: DjangoEmployee | string;
  due_date?: string;
  first_due_date?: string;
  first_reference_period?: string;
  last_reference_period?: string;
  tax_amount?: number;
  total_variable_tax_amount?: number;
  fixed_tax_amount?: number;
  costs?: any[];
  installment_amount?: number;
  insurance_premium_amount?: number;
  total_debt_amount?: number;
  debt_balance?: number;
  balance?: number;
  credited?: string;
  status?: SERVICE_STATUS;
  fixed_tax_rate?: number;
  registered_in_payroll?: boolean;
  is_renegotiable?: boolean;
  is_refinanceable?: boolean;
  reason?: LOAN_REASON;
  balance_for_prepayment?: number;
  refinanceable_balance?: number;
  sum_paid_installments?: number;
  paid_amount?: number;
  // ABSTRACT LOAN
  created?: string;
  modified?: string;
  amount?: number;
  period?: number;
  interest_rate?: number;
  effective_interest_rate?: number;
  fee_amount?: number;
  currency?: string;
}

///////////////
/// PRIVATE ///
///////////////
function bindConstructorDataToInstance(instance: Loan, data: Loan | DjangoLoan) {
  const jsData = bindDjangoPropsToJs(data);
  instance.proposal = parseProposal(jsData.proposal);
  instance.employee = parseEmployee(jsData.employee);
  instance.dueDate = parseDate(jsData.dueDate);
  instance.firstReferencePeriod = jsData.firstReferencePeriod;
  instance.lastReferencePeriod = parseDate(jsData.lastReferencePeriod);
  instance.firstDueDate = parseDate(jsData.firstDueDate);
  instance.taxAmount = jsData.taxAmount;
  instance.totalVariableTaxAmount = jsData.totalVariableTaxAmount;
  instance.fixedTaxAmount = jsData.fixedTaxAmount;
  instance.costs = jsData.costs;
  instance.installmentAmount = jsData.installmentAmount;
  instance.insurancePremiumAmount = jsData.insurancePremiumAmount;
  instance.totalDebtAmount = jsData.totalDebtAmount;
  instance.debtBalance = jsData.debtBalance;
  instance.balance = jsData.balance;
  instance.credited = parseDate(jsData.credited);
  instance.status = jsData.status;
  instance.fixedTaxRate = jsData.fixedTaxRate;
  instance.registeredInPayroll = jsData.registeredInPayroll;
  instance.isRenegotiable = jsData.isRenegotiable;
  instance.isRefinanceable = jsData.isRefinanceable;
  instance.reason = jsData.reason;
  instance.balanceForPrepayment = jsData.balanceForPrepayment;
  instance.refinanceableBalance = jsData.refinanceableBalance;
  instance.sumPaidInstallments = jsData.sumPaidInstallments;
  instance.paidAmount = jsData.paidAmount;
  // ABSTRACT LOAN
  instance.created = parseDate(jsData.created);
  instance.modified = parseDate(jsData.modified);
  instance.amount = jsData.amount;
  instance.period = jsData.period;
  instance.interestRate = jsData.interestRate;
  instance.effectiveInterestRate = jsData.effectiveInterestRate;
  instance.feeAmount = jsData.feeAmount;
  instance.currency = jsData.currency;
  instance.score = jsData.score;
  instance.scoreVersion = jsData.scoreVersion;
  instance.agioAmount = jsData.agioAmount;
}

function bindDjangoPropsToJs(data: Loan | DjangoLoan) {
  const djangoData = data as DjangoLoan;
  const jsData = data as Loan;
  return {
    ...data,
    firstReferencePeriod: djangoData.first_reference_period || jsData.firstReferencePeriod,
    lastReferencePeriod:
      applyTimezone(parseDate(djangoData.last_reference_period), 0) || jsData.lastReferencePeriod,
    dueDate: applyTimezone(parseDate(djangoData.due_date), 0) || jsData.dueDate,
    firstDueDate: applyTimezone(parseDate(djangoData.first_due_date), 0) || jsData.firstDueDate,
    taxAmount: djangoData.tax_amount || jsData.taxAmount,
    totalVariableTaxAmount: djangoData.total_debt_amount || jsData.totalVariableTaxAmount,
    fixedTaxAmount: djangoData.fixed_tax_amount || jsData.fixedTaxAmount,
    installmentAmount: djangoData.installment_amount || jsData.installmentAmount,
    insurancePremiumAmount: djangoData.insurance_premium_amount || jsData.insurancePremiumAmount,
    totalDebtAmount: djangoData.total_debt_amount || jsData.totalDebtAmount,
    debtBalance: djangoData.debt_balance || jsData.debtBalance,
    fixedTaxRate: djangoData.fixed_tax_rate || jsData.fixedTaxRate,
    registeredInPayroll: djangoData.registered_in_payroll || jsData.registeredInPayroll,
    isRenegotiable: djangoData.is_renegotiable || jsData.isRenegotiable,
    isRefinanceable: djangoData.is_refinanceable || jsData.isRefinanceable,
    interestRate: djangoData.interest_rate || jsData.interestRate,
    effectiveInterestRate: djangoData.effective_interest_rate || jsData.effectiveInterestRate,
    feeAmount: djangoData.fee_amount || jsData.feeAmount,
    score: djangoData.score || jsData.score,
    scoreVersion: djangoData.score_version || jsData.scoreVersion,
    agioAmount: djangoData.agio_amount || jsData.agioAmount,
    balanceForPrepayment: djangoData.balance_for_prepayment || jsData.balanceForPrepayment,
    refinanceableBalance: djangoData.refinanceable_balance || jsData.refinanceableBalance,
    sumPaidInstallments: djangoData.sum_paid_installments || jsData.sumPaidInstallments,
    paidAmount: djangoData.paid_amount || jsData.sumPaidInstallments,
  } as Loan;
}

///////////////
/// HELPERS ///
///////////////
function giveupable(loan: Loan): boolean {
  return isServiceGiveupable(loan);
}

function liquidatable(loan: Loan): boolean {
  return SERVICE_LIQUIDATEABLE_STATUSES.includes(loan.status);
}

function ccbDownloadable(loan: Loan): boolean {
  return SERVICE_CCB_DOWNLOADABLE_STATUSES.includes(loan.status);
}

function settableAsRenegotiable(loan: Loan): boolean {
  return loan.status === SERVICE_STATUS.IN_PROGRESS && !loan.isRenegotiable;
}

function settableToPrepayment(loan: Loan): boolean {
  return loan.proposal.proposalType !== PROPOSAL_TYPE.PERSONAL_LOAN;
}
